import React from 'react';
import { Helmet } from 'react-helmet-async';

import { APP_CONFIG, SOCIAL_CONFIG } from '@/config';

interface OrganizationJsonLdProps {
  logo?: string;
}

export const OrganizationJsonLd: React.FC<OrganizationJsonLdProps> = ({ logo }) => (
  <Helmet>
    <script type="application/ld+json">
      {JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: APP_CONFIG.name,
        url: APP_CONFIG.url,
        logo: logo || `${APP_CONFIG.url}/logo.png`,
        description: APP_CONFIG.description,
        sameAs: [SOCIAL_CONFIG.instagram, SOCIAL_CONFIG.youtube, SOCIAL_CONFIG.facebook].filter(
          Boolean
        ),
        contactPoint: [
          {
            '@type': 'ContactPoint',
            telephone: '+919876543210',
            contactType: 'customer service',
            availableLanguage: ['English', 'Hindi'],
          },
        ],
      })}
    </script>
  </Helmet>
);

interface WebSiteJsonLdProps {
  searchUrl?: string;
}

export const WebSiteJsonLd: React.FC<WebSiteJsonLdProps> = ({
  searchUrl = `${APP_CONFIG.url}/search?q={search_term_string}`,
}) => (
  <Helmet>
    <script type="application/ld+json">
      {JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: APP_CONFIG.name,
        url: APP_CONFIG.url,
        description: APP_CONFIG.description,
        potentialAction: {
          '@type': 'SearchAction',
          target: {
            '@type': 'EntryPoint',
            urlTemplate: searchUrl,
          },
          'query-input': 'required name=search_term_string',
        },
      })}
    </script>
  </Helmet>
);

interface BreadcrumbJsonLdProps {
  items: { name: string; url: string }[];
}

export const BreadcrumbJsonLd: React.FC<BreadcrumbJsonLdProps> = ({ items }) => (
  <Helmet>
    <script type="application/ld+json">
      {JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: items.map((item, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          name: item.name,
          item: item.url,
        })),
      })}
    </script>
  </Helmet>
);

interface ProductJsonLdProps {
  name: string;
  description: string;
  image: string;
  sku: string;
  mpn: string;
  brand: string;
  offers: {
    price: number;
    priceCurrency: string;
    availability: 'InStock' | 'OutOfStock' | 'PreOrder';
    url?: string;
  };
  review?: {
    ratingValue: number;
    reviewCount: number;
    bestRating?: number;
  };
}

export const ProductJsonLd: React.FC<ProductJsonLdProps> = ({
  name,
  description,
  image,
  sku,
  mpn,
  brand,
  offers,
  review,
}) => (
  <Helmet>
    <script type="application/ld+json">
      {JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'Product',
        name,
        description,
        image,
        sku,
        mpn,
        brand: { '@type': 'Brand', name: brand },
        offers: {
          '@type': 'Offer',
          price: offers.price,
          priceCurrency: offers.priceCurrency,
          availability: `https://schema.org/${offers.availability}`,
          url: offers.url || APP_CONFIG.url,
        },
        ...(review
          ? {
              aggregateRating: {
                '@type': 'AggregateRating',
                ratingValue: review.ratingValue,
                reviewCount: review.reviewCount,
                bestRating: review.bestRating || 5,
              },
            }
          : {}),
      })}
    </script>
  </Helmet>
);

interface FAQJsonLdProps {
  questions: { question: string; answer: string }[];
}

export const FAQJsonLd: React.FC<FAQJsonLdProps> = ({ questions }) => (
  <Helmet>
    <script type="application/ld+json">
      {JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: questions.map(q => ({
          '@type': 'Question',
          name: q.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: q.answer,
          },
        })),
      })}
    </script>
  </Helmet>
);

interface LocalBusinessJsonLdProps {
  image?: string;
  priceRange?: string;
}

export const LocalBusinessJsonLd: React.FC<LocalBusinessJsonLdProps> = ({
  image = '/og-image.png',
  priceRange = '₹₹',
}) => (
  <Helmet>
    <script type="application/ld+json">
      {JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'Store',
        name: APP_CONFIG.name,
        image: `${APP_CONFIG.url}${image}`,
        url: APP_CONFIG.url,
        telephone: '+919876543210',
        address: {
          '@type': 'PostalAddress',
          addressLocality: 'Mumbai',
          addressRegion: 'MH',
          addressCountry: 'IN',
        },
        priceRange,
        openingHoursSpecification: [
          {
            '@type': 'OpeningHoursSpecification',
            dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
            opens: '09:00',
            closes: '18:00',
          },
        ],
      })}
    </script>
  </Helmet>
);
